<template>
  <div class="tx-login">

    <!-- ===== Animated cosmic background ===== -->
    <div class="tx-bg" aria-hidden="true">
      <div class="tx-aurora tx-aurora--1"></div>
      <div class="tx-aurora tx-aurora--2"></div>
      <div class="tx-aurora tx-aurora--3"></div>
      <div class="tx-grid"></div>
      <div class="tx-stars"></div>

      <!-- animated trading line -->
      <svg class="tx-chart" viewBox="0 0 1440 320" preserveAspectRatio="none">
        <defs>
          <linearGradient id="txLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stop-color="#4da3ff"/>
            <stop offset="55%" stop-color="#7c5cff"/>
            <stop offset="100%" stop-color="#36d6c3"/>
          </linearGradient>
          <linearGradient id="txFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="rgba(94,150,255,0.22)"/>
            <stop offset="100%" stop-color="rgba(94,150,255,0)"/>
          </linearGradient>
        </defs>
        <path class="tx-chart__fill"
              d="M0,260 L120,238 L240,250 L360,198 L480,222 L600,168 L720,184 L840,118 L960,142 L1080,86 L1200,112 L1320,48 L1440,72 L1440,320 L0,320 Z"/>
        <path class="tx-chart__line"
              d="M0,260 L120,238 L240,250 L360,198 L480,222 L600,168 L720,184 L840,118 L960,142 L1080,86 L1200,112 L1320,48 L1440,72"/>
      </svg>
    </div>

    <!-- ===== Login card ===== -->
    <div class="tx-card">
      <div class="tx-card__sheen"></div>

      <!-- Brand -->
      <div class="tx-brand">
        <b-img :src="appLogoImage" class="tx-brand__logo" alt="logo"/>
        <b-img :src="appNameImage" class="tx-brand__name" alt="Trade Access"/>
      </div>

      <h1 class="tx-title">{{ $t('login.welcome') }} <span class="tx-wave">👋</span></h1>
      <p class="tx-subtitle">{{ $t('login.welcomeDesc') }}</p>

      <!-- form -->
      <validation-observer ref="loginValidation">
        <b-form class="tx-form" @submit.prevent>

          <!-- username -->
          <validation-provider #default="{ errors }" name="username" rules="required">
            <label class="tx-label" for="login-username">{{ $t('login.username') }}</label>
            <div class="tx-field" :class="{ 'tx-field--err': errors.length > 0 }">
              <feather-icon icon="UserIcon" size="18" class="tx-field__icon"/>
              <b-form-input
                id="login-username"
                v-model="username"
                :state="errors.length > 0 ? false : null"
                name="login-username"
                class="tx-field__input"
                placeholder="john"
                autocomplete="username"
              />
            </div>
            <small class="tx-err">{{ errors[0] }}</small>
          </validation-provider>

          <!-- password -->
          <validation-provider #default="{ errors }" name="Password" rules="required">
            <div class="tx-label-row">
              <label class="tx-label" for="login-password">{{ $t('login.password') }}</label>
              <b-link class="tx-link" @click.prevent="openForgotPassword">
                {{ $t('login.forgotPassword') }}?
              </b-link>
            </div>
            <div class="tx-field" :class="{ 'tx-field--err': errors.length > 0 }">
              <feather-icon icon="LockIcon" size="18" class="tx-field__icon"/>
              <b-form-input
                id="login-password"
                v-model="password"
                :state="errors.length > 0 ? false : null"
                :type="passwordFieldType"
                name="login-password"
                class="tx-field__input tx-field__input--pw"
                placeholder="············"
                autocomplete="current-password"
                @keyup.enter="validationForm"
              />
              <feather-icon
                :icon="passwordToggleIcon"
                size="18"
                class="tx-field__eye"
                @click="togglePasswordVisibility"
              />
            </div>
            <small class="tx-err">{{ errors[0] }}</small>
          </validation-provider>

          <!-- remember me -->
          <div class="tx-row">
            <label class="tx-check">
              <input id="remember-me" v-model="status" type="checkbox"/>
              <span class="tx-check__box"><feather-icon icon="CheckIcon" size="12"/></span>
              <span class="tx-check__label">{{ $t('login.remember') }}</span>
            </label>
          </div>

          <!-- submit -->
          <button type="submit" class="tx-submit" :disabled="loading" @click="validationForm">
            <b-spinner v-if="loading" small class="mr-50"/>
            <span>{{ loading ? $t('login.loggingIn') : $t('login.submit') }}</span>
            <feather-icon v-if="!loading" icon="ArrowRightIcon" size="18" class="tx-submit__arrow"/>
          </button>

        </b-form>
      </validation-observer>
    </div>

    <!-- ===== Password expired modal (unchanged logic) ===== -->
    <b-modal ref="passwordExpiredModal"
             no-close-on-backdrop
             no-close-on-esc
             hide-header-close
             :hide-footer="true"
             centered>
      <template #modal-title>{{ $t('login.passwordExpiredTitle') }}</template>
      <p>{{ $t('login.passwordExpiredBody') }}</p>
      <div class="d-flex align-items-center my-3">
        <code class="flex-grow-1 p-2 bg-light text-dark" style="font-size: 1.1rem; word-break: break-all;">
          {{ expiredPassword }}
        </code>
        <b-button size="sm" variant="outline-primary" class="ml-2" @click="copyPassword">
          {{ $t('login.copy') }}
        </b-button>
      </div>
      <b-form-checkbox v-model="acknowledged">
        {{ $t('login.passwordExpiredAck') }}
      </b-form-checkbox>
      <div class="text-right mt-3">
        <b-button variant="primary" :disabled="!acknowledged" @click="onAcknowledge">
          {{ $t('login.continue') }}
        </b-button>
      </div>
    </b-modal>

    <!-- ===== Forgot password modal (unchanged logic) ===== -->
    <b-modal ref="forgotPasswordModal"
             hide-header
             hide-footer
             centered
             modal-class="tx-modal"
             body-class="tx-modal__bodywrap">
      <div class="txm">
        <button type="button" class="txm__close" @click="$refs.forgotPasswordModal.hide()">
          <feather-icon icon="XIcon" size="18"/>
        </button>

        <div class="txm__hero">
          <span class="txm__icon"><feather-icon icon="KeyIcon" size="26"/></span>
          <h3 class="txm__title">{{ $t('login.forgotPasswordTitle') }}</h3>
          <p class="txm__desc">{{ $t('login.forgotPasswordDesc') }}</p>
        </div>

        <b-form class="txm__form" @submit.prevent="submitForgotPassword">
          <label class="txm__label" for="forgot-username">{{ $t('login.username') }}</label>
          <div class="txm__field">
            <feather-icon icon="UserIcon" size="18" class="txm__field-icon"/>
            <b-form-input
              id="forgot-username"
              v-model="forgotUsername"
              name="forgot-username"
              class="txm__input"
              placeholder="john"
              autofocus
            />
          </div>

          <span class="txm__mode-label">{{ $t('login.forgotPasswordMode') }}</span>
          <div class="txm__options">
            <label class="txm__opt" :class="{ 'is-active': forgotNewPassword === false }">
              <input v-model="forgotNewPassword" type="radio" :value="false"/>
              <span class="txm__radio"></span>
              <span class="txm__opt-text">
                <strong>{{ $t('login.forgotPasswordOneTime') }}</strong>
                <small>{{ $t('login.forgotPasswordOneTimeDesc') }}</small>
              </span>
            </label>
            <label class="txm__opt" :class="{ 'is-active': forgotNewPassword === true }">
              <input v-model="forgotNewPassword" type="radio" :value="true"/>
              <span class="txm__radio"></span>
              <span class="txm__opt-text">
                <strong>{{ $t('login.forgotPasswordNew') }}</strong>
                <small>{{ $t('login.forgotPasswordNewDesc') }}</small>
              </span>
            </label>
          </div>

          <button type="submit" class="txm__submit" :disabled="forgotLoading || !forgotUsername">
            <b-spinner v-if="forgotLoading" small class="mr-50"/>
            <feather-icon v-else icon="SendIcon" size="16" class="mr-50"/>
            {{ $t('login.forgotPasswordSend') }}
          </button>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { $themeConfig } from '@themeConfig'
import { mapActions } from 'vuex'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinBase from '@/components/mixin/mixinBase'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility,mixinBase, mixinNotifications],
  data() {
    return {
      status: '',
      password: '',
      username: '',
      loading: false,
      sideImg: require('@/assets/images/pages/trade.png'),
      expiredPassword: '',
      acknowledged: false,
      forgotUsername: '',
      forgotNewPassword: false,
      forgotLoading: false,
      // validation rulesimport store from '@/store/index'
      required,
      email,
    }
  },
  mounted() {
    this.tryConnection();
  },
  setup() {

    // App Name
    const { appName, appNameImage, appLogoImage } = $themeConfig.app
    return {
      appName,
      appLogoImage,
      appNameImage,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/trade1.jpg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  methods: {
    ...mapActions('user', ['login','checkConnection','forgotPassword']),
    openForgotPassword() {
      this.forgotUsername = this.username
      this.forgotNewPassword = false
      this.$refs.forgotPasswordModal.show()
    },
    submitForgotPassword() {
      if (!this.forgotUsername || this.forgotLoading) {
        return
      }
      this.forgotLoading = true
      this.forgotPassword({ username: this.forgotUsername, newPassword: this.forgotNewPassword })
          .then(() => {
            this.$refs.forgotPasswordModal.hide()
            this.showSuccessNotification(this.$t('login.forgotPasswordSuccess'), this.$t('login.forgotPasswordSuccessDesc'))
          })
          .catch(err => {
            this.showErrorNotification(this.$t('login.forgotPasswordError'), this.getErrorDescription(err))
          })
          .finally(() => {
            this.forgotLoading = false
          })
    },
    tryConnection(){
      let $this = this;
      this.checkConnection().then(()=>$this.redirect('/'))
    },
    async validationForm() {
      let $this = this;
      if (this.loading) {
        return
      }
      let success = await this.$refs.loginValidation.validate()
      if (!success) {
        return
      }
      this.loading = true
      this.login({ username: this.username, password: this.password, remember: this.status })
            .then(result => {
              if (result && result.passwordExpired) {
                $this.expiredPassword = result.generatedPassword
                $this.acknowledged = false
                $this.$refs.passwordExpiredModal.show()
                return
              }
              $this.showSuccessNotification(this.$t('login.loginSuccess'), this.$t('login.loginSuccessDesc'))
              $this.redirect('/')
              $this.reminderTodayActivities();
          })
          .catch(err=> {
            console.log("Login failed",err)
            $this.showErrorNotification($this.$t('login.loginError'), this.getErrorDescription(err))
          })
          .finally(() => {
            $this.loading = false
          })

    },
    copyPassword() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.expiredPassword)
            .then(() => this.showSuccessNotification(this.$t('login.copied'), ''))
            .catch(() => {})
      }
    },
    onAcknowledge() {
      this.$refs.passwordExpiredModal.hide()
      this.password = ''
      this.expiredPassword = ''
      this.acknowledged = false
    },
    redirect(defaultRedirect) {
      let url = new URL(window.location.href);
      let returnUrl = url.searchParams.get("returnUrl");
      if (returnUrl != null && returnUrl !== "") {
        returnUrl = returnUrl.trim();
        //even that vue check for xss attack we made a double check
        //remove all http or other protocols
        returnUrl = returnUrl.replace("http://", "").replace("https://", "").replace("://", "");
        //check for points
        returnUrl = returnUrl.replace(".", "");
        this.$router.push(returnUrl)
      } else
        this.$router.push(defaultRedirect)
    },
    async reminderTodayActivities() {
      const $this = this
      try {
        let activities = []
        try {
          activities = await $this.$store.dispatch('calendar/getTodayUpcomingEvents')
        } catch (err) {
          // If the dedicated action is not available, warn and bail out gracefully
          console.warn('[reminderTodayActivities] calendar action failed', err)
          $this.showInfoNotification($this.$t('calendar.today') || 'Today', $this.$t('calendar.noIntegration', 'No calendar integration available to show activities.'))
          return
        }

        if (!Array.isArray(activities) || activities.length === 0) {
          // Nothing to show — silently return
          return
        }

        // Format up to the first 6 activities into a short, user-friendly message
        const take = 6
        const fmtTime = iso => {
          if (!iso) return ''
          try {
            return $this.$moment(iso).format('HH:mm')
          } catch (e) {
            return iso
          }
        }

        const lines = activities.slice(0, take).map((a, idx) => {
          const start = a.startTime || a.start || a.begin || a.from
          const end = a.endTime || a.end || a.to
          // Prefer client name if present (matches CalendarEventsDropdown.getEventTitle)
          let title = a.title || null
          if (!title && a.clientNames != null) {
            // clientNames is an object; show the first value
            const vals = Object.values(a.clientNames)
            title = vals.length > 0 ? vals[0] : null
            if (title) {
              title = ($this.$t ? $this.$t(`calendarTypes.${a.type || 'EVENT'}.name`) : (a.type || 'Event')) + ' - ' + title
            }
          }
          if (!title) title = a.title || a.summary || a.name || a.subject || $this.$t('calendar.untitled') || 'Untitled'
          const desc = a.description || a.body || '';
          const startTime = fmtTime(start)
          const endTime = fmtTime(end)
          const time = start ? startTime + ((startTime !== endTime) && end ? '–' + endTime : '') : ''
          return `${idx + 1}) ${time ? time + ' : ' : ''}${title}${desc ? ' — ' + desc : ''}`
        })

        if (activities.length > take) lines.push(`...and ${activities.length - take} more`)

        const message = lines.join('\n')

        // Use the notifications mixin to show the message. Use a stickId so
        // repeated logins won't stack identical notifications.
        const dateLabel = $this.$moment().format('dddd, D MMM YYYY')
        const title = $this.$t('calendar.todayActivities', 'Today\'s activities') + ' — ' + dateLabel
        const stickId = `daily-cal-${$this.$moment().format('YYYY-MM-DD')}`

        $this.showInfoNotification(title, message, stickId)
      } catch (err) {
        console.error('[reminderTodayActivities] failed to fetch or display activities', err)
        $this.showErrorNotification($this.$t('menu.calendar') || 'Calendar', $this.$t('globalMessages.loadError') || 'Failed to load today\'s activities')
      }

    }
  },
}
</script>

<style lang="scss" scoped>
$tx-bg-1: #060818;
$tx-bg-2: #0b1030;
$tx-accent: #5e96ff;
$tx-accent-2: #7c5cff;
$tx-accent-3: #36d6c3;

.tx-login {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: radial-gradient(140% 120% at 80% -10%, #131a45 0%, $tx-bg-2 38%, $tx-bg-1 100%);
  font-family: 'Montserrat', 'Segoe UI', sans-serif;
  padding: 1.5rem;
}

/* ---------- Background layers ---------- */
.tx-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

.tx-aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.55;
  will-change: transform;

  &--1 {
    width: 46vw; height: 46vw;
    top: -14vw; left: -8vw;
    background: radial-gradient(circle, rgba(94,150,255,0.9), transparent 65%);
    animation: tx-drift-1 18s ease-in-out infinite alternate;
  }
  &--2 {
    width: 40vw; height: 40vw;
    bottom: -12vw; right: -6vw;
    background: radial-gradient(circle, rgba(124,92,255,0.85), transparent 65%);
    animation: tx-drift-2 22s ease-in-out infinite alternate;
  }
  &--3 {
    width: 34vw; height: 34vw;
    top: 30%; left: 42%;
    background: radial-gradient(circle, rgba(54,214,195,0.55), transparent 65%);
    animation: tx-drift-3 26s ease-in-out infinite alternate;
  }
}

.tx-grid {
  position: absolute;
  inset: -2px;
  background-image:
    linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px);
  background-size: 54px 54px;
  mask-image: radial-gradient(circle at 50% 45%, #000 0%, transparent 72%);
  -webkit-mask-image: radial-gradient(circle at 50% 45%, #000 0%, transparent 72%);
  animation: tx-grid-pan 40s linear infinite;
}

.tx-stars {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1.5px 1.5px at 20% 30%, rgba(255,255,255,0.7), transparent),
    radial-gradient(1.5px 1.5px at 70% 20%, rgba(255,255,255,0.55), transparent),
    radial-gradient(1px 1px at 40% 70%, rgba(255,255,255,0.6), transparent),
    radial-gradient(1px 1px at 85% 60%, rgba(255,255,255,0.5), transparent),
    radial-gradient(1.5px 1.5px at 55% 85%, rgba(255,255,255,0.45), transparent),
    radial-gradient(1px 1px at 10% 80%, rgba(255,255,255,0.5), transparent);
  animation: tx-twinkle 6s ease-in-out infinite alternate;
}

.tx-chart {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 42vh;
  opacity: 0.9;

  &__fill { fill: url(#txFill); }
  &__line {
    fill: none;
    stroke: url(#txLine);
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0 0 10px rgba(94,150,255,0.55));
    stroke-dasharray: 3200;
    stroke-dashoffset: 3200;
    animation: tx-draw 4s cubic-bezier(0.65, 0, 0.35, 1) 0.3s forwards,
               tx-float 9s ease-in-out 4.5s infinite alternate;
  }
}

/* ---------- Card ---------- */
.tx-card {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 430px;
  padding: 2.6rem 2.6rem 2.4rem;
  border-radius: 26px;
  background: linear-gradient(165deg, rgba(28,34,72,0.78) 0%, rgba(14,18,46,0.82) 100%);
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow:
    0 40px 90px -30px rgba(0,0,0,0.75),
    0 0 0 1px rgba(255,255,255,0.04) inset,
    0 1px 0 rgba(255,255,255,0.10) inset;
  backdrop-filter: blur(22px) saturate(140%);
  -webkit-backdrop-filter: blur(22px) saturate(140%);
  overflow: hidden;
  animation: tx-rise 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;

  &__sheen {
    position: absolute;
    top: -60%;
    left: -30%;
    width: 80%;
    height: 200%;
    background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.10) 50%, transparent 70%);
    transform: rotate(8deg);
    pointer-events: none;
    animation: tx-sheen 7s ease-in-out 1.2s infinite;
  }
}

.tx-brand {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1.6rem;

  &__logo { max-height: 42px; width: auto; }
  &__name { max-height: 34px; width: auto; filter: brightness(0) invert(1); opacity: 0.95; }
}

.tx-title {
  color: #fff;
  font-size: 1.7rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-bottom: 0.35rem;
}
.tx-wave { display: inline-block; transform-origin: 70% 70%; animation: tx-wave 2.6s ease-in-out 1s infinite; }

.tx-subtitle {
  color: rgba(255,255,255,0.6);
  font-size: 0.92rem;
  margin-bottom: 1.8rem;
}

.tx-form { display: block; }

.tx-label {
  display: block;
  color: rgba(255,255,255,0.78);
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 0.45rem;
  letter-spacing: 0.01em;
}
.tx-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.15rem;
}
.tx-link {
  color: $tx-accent;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
  &:hover { color: lighten($tx-accent, 12%); text-decoration: underline; }
}

/* fields */
.tx-field {
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 14px;
  background: rgba(255,255,255,0.05);
  border: 1.5px solid rgba(255,255,255,0.12);
  transition: border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;

  &__icon {
    position: absolute;
    left: 14px;
    color: rgba(255,255,255,0.5);
    pointer-events: none;
    transition: color 0.22s ease;
  }
  &__eye {
    position: absolute;
    right: 14px;
    color: rgba(255,255,255,0.55);
    cursor: pointer;
    transition: color 0.18s ease;
    &:hover { color: $tx-accent; }
  }

  &:focus-within {
    border-color: rgba(94,150,255,0.85);
    background: rgba(94,150,255,0.08);
    box-shadow: 0 0 0 4px rgba(94,150,255,0.16);
    .tx-field__icon { color: $tx-accent; }
  }

  &--err {
    border-color: rgba(255,99,120,0.85);
    box-shadow: 0 0 0 4px rgba(255,99,120,0.14);
  }
}

/* override bootstrap-vue form-control inside our field */
.tx-field ::v-deep .tx-field__input.form-control,
.tx-field__input {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  color: #fff !important;
  height: 52px;
  padding: 0 16px 0 44px;
  font-size: 0.95rem;
  border-radius: 14px;

  &::placeholder { color: rgba(255,255,255,0.35); }
  &:focus { background: transparent !important; box-shadow: none !important; color: #fff !important; }
}
.tx-field__input--pw { padding-right: 44px; }

.tx-err {
  display: block;
  min-height: 1rem;
  margin-top: 0.35rem;
  color: #ff8a9b;
  font-size: 0.75rem;
}

/* remember me */
.tx-row {
  display: flex;
  align-items: center;
  margin: 0.4rem 0 1.6rem;
}
.tx-check {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  cursor: pointer;
  user-select: none;
  margin: 0;

  input { position: absolute; opacity: 0; width: 0; height: 0; }
  &__box {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1.5px solid rgba(255,255,255,0.28);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    background: rgba(255,255,255,0.04);
    transition: all 0.18s ease;
  }
  &__label { color: rgba(255,255,255,0.7); font-size: 0.85rem; }

  input:checked + &__box {
    background: linear-gradient(135deg, $tx-accent, $tx-accent-2);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 4px 12px -3px rgba(94,150,255,0.6);
  }
}

/* submit */
.tx-submit {
  position: relative;
  width: 100%;
  height: 54px;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(120deg, $tx-accent 0%, $tx-accent-2 55%, $tx-accent-3 130%);
  background-size: 180% 180%;
  box-shadow: 0 14px 34px -12px rgba(94,150,255,0.75), 0 0 0 1px rgba(255,255,255,0.08) inset;
  transition: transform 0.2s ease, box-shadow 0.25s ease, background-position 0.5s ease;
  overflow: hidden;

  &__arrow { transition: transform 0.25s ease; }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background-position: 100% 0;
    box-shadow: 0 20px 44px -12px rgba(94,150,255,0.9), 0 0 0 1px rgba(255,255,255,0.12) inset;
    .tx-submit__arrow { transform: translateX(4px); }
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.75; cursor: progress; }
}

/* ---------- Keyframes ---------- */
@keyframes tx-rise {
  from { opacity: 0; transform: translateY(26px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes tx-draw { to { stroke-dashoffset: 0; } }
@keyframes tx-float {
  from { transform: translateY(0); }
  to   { transform: translateY(-10px); }
}
@keyframes tx-sheen {
  0%   { transform: translateX(-120%) rotate(8deg); }
  60%, 100% { transform: translateX(260%) rotate(8deg); }
}
@keyframes tx-wave {
  0%, 60%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-8deg); }
  30% { transform: rotate(14deg); }
  40% { transform: rotate(-4deg); }
  50% { transform: rotate(10deg); }
}
@keyframes tx-drift-1 {
  from { transform: translate(0, 0) scale(1); }
  to   { transform: translate(6vw, 4vw) scale(1.12); }
}
@keyframes tx-drift-2 {
  from { transform: translate(0, 0) scale(1); }
  to   { transform: translate(-5vw, -4vw) scale(1.15); }
}
@keyframes tx-drift-3 {
  from { transform: translate(0, 0) scale(1); }
  to   { transform: translate(-4vw, 5vw) scale(1.1); }
}
@keyframes tx-grid-pan {
  from { background-position: 0 0; }
  to   { background-position: 54px 54px; }
}
@keyframes tx-twinkle {
  from { opacity: 0.5; }
  to   { opacity: 0.9; }
}

@media (max-width: 480px) {
  .tx-card { padding: 2rem 1.5rem; border-radius: 22px; }
  .tx-title { font-size: 1.45rem; }
}

@media (prefers-reduced-motion: reduce) {
  .tx-aurora, .tx-grid, .tx-stars, .tx-chart__line, .tx-card__sheen, .tx-wave, .tx-card {
    animation: none !important;
  }
  .tx-chart__line { stroke-dashoffset: 0; }
}
</style>

<!-- Global (un-scoped) styling for the forgot-password modal, which BootstrapVue
     can render outside this component's scoped subtree. -->
<style lang="scss">
.tx-modal {
  $accent: #5e96ff;
  $accent-2: #7c5cff;
  $accent-3: #36d6c3;

  .modal-dialog { max-width: 446px; }

  .modal-dialog .modal-content {
    position: relative;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 24px;
    background: linear-gradient(165deg, rgba(30, 37, 78, 0.98) 0%, rgba(13, 17, 44, 0.99) 100%);
    box-shadow: 0 40px 90px -30px rgba(0, 0, 0, 0.85), 0 1px 0 rgba(255, 255, 255, 0.08) inset;
    overflow: hidden;
    color: #fff;
  }

  .modal-body { padding: 0; }

  .txm {
    position: relative;

    &__close {
      position: absolute;
      top: 14px;
      right: 14px;
      z-index: 3;
      width: 34px;
      height: 34px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.65);
      cursor: pointer;
      transition: all 0.18s ease;
      &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
    }

    &__hero {
      position: relative;
      text-align: center;
      padding: 2.1rem 2rem 1.2rem;
      overflow: hidden;
      &::after {
        content: '';
        position: absolute;
        top: -90px;
        left: 50%;
        transform: translateX(-50%);
        width: 260px;
        height: 200px;
        background: radial-gradient(circle, rgba(94, 150, 255, 0.30), transparent 70%);
        pointer-events: none;
      }
    }

    &__icon {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
      margin-bottom: 1rem;
      border-radius: 18px;
      color: #fff;
      background: linear-gradient(135deg, $accent, $accent-2);
      box-shadow: 0 14px 30px -10px rgba(94, 150, 255, 0.7);
    }

    &__title {
      position: relative;
      z-index: 1;
      color: #fff;
      font-size: 1.3rem;
      font-weight: 700;
      margin-bottom: 0.4rem;
    }
    &__desc {
      position: relative;
      z-index: 1;
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.86rem;
      line-height: 1.5;
      margin: 0 auto;
      max-width: 330px;
    }

    &__form { padding: 0.4rem 2rem 2.1rem; }

    &__label,
    &__mode-label {
      display: block;
      color: rgba(255, 255, 255, 0.78);
      font-size: 0.8rem;
      font-weight: 600;
      margin-bottom: 0.45rem;
    }
    &__mode-label { margin-top: 1.3rem; }

    &__field {
      position: relative;
      display: flex;
      align-items: center;
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.05);
      border: 1.5px solid rgba(255, 255, 255, 0.12);
      transition: border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
      &:focus-within {
        border-color: rgba(94, 150, 255, 0.85);
        background: rgba(94, 150, 255, 0.08);
        box-shadow: 0 0 0 4px rgba(94, 150, 255, 0.16);
        .txm__field-icon { color: $accent; }
      }
    }
    &__field-icon {
      position: absolute;
      left: 14px;
      color: rgba(255, 255, 255, 0.5);
      pointer-events: none;
      transition: color 0.22s ease;
    }
    &__input.form-control {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      color: #fff !important;
      height: 50px;
      padding: 0 16px 0 44px;
      font-size: 0.95rem;
      border-radius: 14px;
      &::placeholder { color: rgba(255, 255, 255, 0.35); }
    }

    &__options {
      display: flex;
      flex-direction: column;
      gap: 0.6rem;
    }
    &__opt {
      position: relative;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin: 0;
      padding: 0.85rem 0.95rem;
      border-radius: 14px;
      cursor: pointer;
      background: rgba(255, 255, 255, 0.04);
      border: 1.5px solid rgba(255, 255, 255, 0.10);
      transition: all 0.2s ease;

      input { position: absolute; opacity: 0; width: 0; height: 0; }

      &:hover { border-color: rgba(255, 255, 255, 0.2); background: rgba(255, 255, 255, 0.06); }

      &.is-active {
        border-color: rgba(94, 150, 255, 0.85);
        background: rgba(94, 150, 255, 0.10);
        box-shadow: 0 0 0 3px rgba(94, 150, 255, 0.12);
      }
    }
    &__radio {
      flex-shrink: 0;
      margin-top: 2px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.3);
      position: relative;
      transition: all 0.2s ease;
    }
    &__opt.is-active &__radio {
      border-color: $accent;
      &::after {
        content: '';
        position: absolute;
        inset: 3px;
        border-radius: 50%;
        background: linear-gradient(135deg, $accent, $accent-2);
      }
    }
    &__opt-text {
      display: flex;
      flex-direction: column;
      strong { color: #fff; font-size: 0.88rem; font-weight: 600; }
      small { color: rgba(255, 255, 255, 0.55); font-size: 0.76rem; line-height: 1.4; margin-top: 2px; }
    }

    &__submit {
      width: 100%;
      height: 50px;
      margin-top: 1.6rem;
      border: none;
      border-radius: 14px;
      cursor: pointer;
      color: #fff;
      font-size: 0.95rem;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(120deg, $accent 0%, $accent-2 55%, $accent-3 130%);
      background-size: 180% 180%;
      box-shadow: 0 14px 34px -12px rgba(94, 150, 255, 0.75);
      transition: transform 0.2s ease, box-shadow 0.25s ease, background-position 0.5s ease;
      &:hover:not(:disabled) {
        transform: translateY(-2px);
        background-position: 100% 0;
        box-shadow: 0 20px 44px -12px rgba(94, 150, 255, 0.9);
      }
      &:disabled { opacity: 0.5; cursor: not-allowed; }
    }
  }
}
</style>
